import { useDynamicConfig } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import type { SavePromotion_cart_savePromotion } from '@/queries/types/SavePromotion';

import { usePDPContext } from '../_contexts/PDPProvider';
import { useWizardStateContext } from '../_contexts/WizardStateProvider';
import getDiscountMessage from '../_utils/discount/getDiscountMessage';
import type { PdpDiscountData, UiDiscount } from '../_utils/discount/types';
import showDiscountMessage from '../_utils/showDiscountMessage';
import useFormattedDiscountPreview from './useFormattedDiscountPreview';

const PROMOTION_DATA = 'promotion';

interface UseDiscountMessage {
  displayMessage: string | null;
}

const useDiscountMessage = (): UseDiscountMessage => {
  const [promotionData, setPromotionData] = useState<
    SavePromotion_cart_savePromotion | undefined
  >(undefined);
  const t = useTranslations();
  const sitewideDiscountConfg = useDynamicConfig('sitewide_discount');
  const discountConfig = useDynamicConfig('discount_user_interface');
  const sitewideDiscount = sitewideDiscountConfg.get('sitewideDiscount', '');
  const discountArray = discountConfig.get('discountArray', []) as UiDiscount[];

  useEffect(() => {
    const data = localStorage.getItem(PROMOTION_DATA) ?? '{}';
    const _promotionData = JSON.parse(data) as
      | SavePromotion_cart_savePromotion
      | undefined;
    setPromotionData(_promotionData);
  }, []);

  const {
    appliedDiscountCode,
    discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
  } = usePDPContext();
  const { isOuraRing4Wizard, isAccessory } = useWizardStateContext();

  const showDiscount = showDiscountMessage({
    appliedDiscountCode,
    discount,
    discountIsSuccess,
    isOuraRing4Wizard,
    isAccessory,
  });

  const discountData: PdpDiscountData = {
    discount,
    campaign: discount?.campaign,
    appliedDiscountCode,
    pdpHandle: discount?.defaultPdpUrl,
    promotionCodes,
  };

  const { campaign } = discountData;
  const discountFound = discountArray.find(
    (discountedObj) => discountedObj.discountCode === appliedDiscountCode,
  );
  const campaignFound = discountArray.find(
    (discountedObj) => discountedObj.discountCode === campaign,
  );

  let discountMessage = getDiscountMessage({
    discountData,
    discountIsLoading,
    discountIsSuccess,
    promotionData,
    sitewideDiscount,
  });

  if (discountFound) {
    discountMessage = discountFound.discountBanner;
  } else if (campaignFound) {
    discountMessage = campaignFound.discountBanner;
  }

  const { isLoading: formattedAmountIsLoading, formattedAmount } =
    useFormattedDiscountPreview(appliedDiscountCode);

  let displayMessage = null;

  if (discountMessage && !formattedAmountIsLoading && showDiscount) {
    if (formattedAmount) {
      displayMessage = t(discountMessage, { amount: formattedAmount });
    } else {
      displayMessage = t(discountMessage);
    }
  }

  return { displayMessage };
};

export default useDiscountMessage;
